import React, { Component } from 'react';
import PropTypes from 'prop-types';
import InputTextV6 from '@Common/InputTextV6/InputTextV6';
import DatePickerV6 from '@Common/DatePickerV6/DatePickerV6';
import { DATE_FORMAT } from '@Constants/constants';
import * as actionTypes from '@Constants/actionTypes';
import Moment from 'moment';
import ToggleV6 from '@Components/Common/ToggleV6/ToggleV6';
import MultiSelectBoxV6 from '@Components/Common/MultiSelectBoxV6/MultiSelectBoxV6';
import Modal61 from '@Components/Common/Modal/Modal61';
import Notification from '@Components/Common/Notification';

export default class LocationForm extends Component {
    constructor(props) {
        super(props);
        
        // Create refs for modals
        this.managementCompanyModal = React.createRef();
        this.regionModal = React.createRef();
        this.areaModal = React.createRef();
        this.continentModal = React.createRef();
        this.typeModal = React.createRef();
        
        this.state = {
            // Form states for each modal
            newManagementCompanyName: '',
            newManagementCompanyCode: '',
            newRegionName: '',
            newAreaName: '',
            newContinentName: '',
            newTypeName: '',
            
            // Loading states for each modal
            isManagementCompanyModalLoading: false,
            isRegionModalLoading: false,
            isAreaModalLoading: false,
            isContinentModalLoading: false,
            isTypeModalLoading: false,
            
            // Form validation states
            isManagementCompanyFormValid: true,
            isRegionFormValid: true,
            isAreaFormValid: true,
            isContinentFormValid: true,
            isTypeFormValid: true,
        };
    }
    onFormChange = (e) => {
        this.props.onFormChange(e.target.name, e.target.value);
    };

    onSingleSelectBoxChange = (id, name) => {
        this.props.onFormChange(name, id.toString());
    };

    onMultiSelectBoxChange = (csv, name) => {
        const newValue = csv ? csv.split(',') : [];
        this.props.onFormChange(name, newValue);
    };

    onDateChange = (value, name) => {
        this.props.onFormChange(name, value);
    };

    onToggleChange = (name, value) => {
        this.props.onFormChange(name, value);
    };

    // Add New functionality handlers
    getOptionsWithAddNew = (options, type) => {
        const addNewOption = {
            id: -9999,
            name: `+ Add New ${type}`,
            styles: {
                backgroundColor: '#F7F5FF',
                color: '#46389E',
            },
        };
        return [...options, addNewOption];
    };

    onManagementCompanyChange = (value, name) => {
        if (value === '-9999') {
            this.setState({ 
                newManagementCompanyName: '', 
                newManagementCompanyCode: '', 
                isManagementCompanyFormValid: true 
            });
            this.managementCompanyModal.current.show();
        } else {
            this.props.onFormChange(name, value);
        }
    };

    onRegionChange = (value, name) => {
        if (value === '-9999') {
            this.setState({ newRegionName: '', isRegionFormValid: true });
            this.regionModal.current.show();
        } else {
            this.props.onFormChange(name, value);
        }
    };

    onAreaChange = (value, name) => {
        if (value === '-9999') {
            this.setState({ newAreaName: '', isAreaFormValid: true });
            this.areaModal.current.show();
        } else {
            this.props.onFormChange(name, value);
        }
    };

    onContinentChange = (value, name) => {
        if (value === '-9999') {
            this.setState({ newContinentName: '', isContinentFormValid: true });
            this.continentModal.current.show();
        } else {
            this.props.onFormChange(name, value);
        }
    };

    onTypeChange = (value, name) => {
        if (value === '-9999') {
            this.setState({ newTypeName: '', isTypeFormValid: true });
            this.typeModal.current.show();
        } else {
            this.props.onFormChange(name, value);
        }
    };

    // Modal creation handlers
    createManagementCompany = async () => {
        if (!this.validateCreateForm('managementCompany')) return false;

        this.setState({ isManagementCompanyModalLoading: true });

        const managementCompany = {
            id: 0,
            name: this.state.newManagementCompanyName,
            companyCode: this.state.newManagementCompanyCode,
            isActive: true,
        };

        try {
            const result = await this.props.createManagementCompany(managementCompany);
            
            if (result.type === actionTypes.CREATE_MGMTCOMPANY_SUCCESS) {
                Notification('success', 'Save Successful', 'Management Company created');
                this.setState({ 
                    isManagementCompanyModalLoading: false,
                    newManagementCompanyName: '',
                    newManagementCompanyCode: ''
                });
                this.managementCompanyModal.current.hide();
                await this.props.reloadFilters();
                // Set the newly created item as selected
                if (result.payload && result.payload.id) {
                    this.props.onFormChange('managementCompanyId', result.payload.id.toString());
                }
            } else {
                Notification('error', 'An Error Occurred', result.payload?.message || 'Failed to create Management Company');
                this.setState({ isManagementCompanyModalLoading: false });
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error.message);
            this.setState({ isManagementCompanyModalLoading: false });
        }
    };

    createRegion = async () => {
        if (!this.validateCreateForm('region')) return false;

        this.setState({ isRegionModalLoading: true });

        const region = {
            id: 0,
            name: this.state.newRegionName,
            isActive: true,
        };

        try {
            const result = await this.props.createRegion(region);
            
            if (result.type === actionTypes.CREATE_REGION_SUCCESS) {
                Notification('success', 'Save Successful', 'Region created');
                this.setState({ 
                    isRegionModalLoading: false,
                    newRegionName: ''
                });
                this.regionModal.current.hide();
                await this.props.reloadFilters();
                // Set the newly created item as selected
                if (result.payload && result.payload.id) {
                    this.props.onFormChange('regionId', result.payload.id.toString());
                }
            } else {
                Notification('error', 'An Error Occurred', result.payload?.message || 'Failed to create Region');
                this.setState({ isRegionModalLoading: false });
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error.message);
            this.setState({ isRegionModalLoading: false });
        }
    };

    createArea = async () => {
        if (!this.validateCreateForm('area')) return false;

        this.setState({ isAreaModalLoading: true });

        const area = {
            id: 0,
            name: this.state.newAreaName,
            isActive: true,
        };

        try {
            const result = await this.props.createArea(area);
            
            if (result.type === actionTypes.CREATE_LOCATIONAREA_SUCCESS) {
                Notification('success', 'Save Successful', 'Area created');
                this.setState({ 
                    isAreaModalLoading: false,
                    newAreaName: ''
                });
                this.areaModal.current.hide();
                await this.props.reloadFilters();
                // Set the newly created item as selected
                if (result.payload && result.payload.id) {
                    this.props.onFormChange('areaId', result.payload.id.toString());
                }
            } else {
                Notification('error', 'An Error Occurred', result.payload?.message || 'Failed to create Area');
                this.setState({ isAreaModalLoading: false });
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error.message);
            this.setState({ isAreaModalLoading: false });
        }
    };

    createContinent = async () => {
        if (!this.validateCreateForm('continent')) return false;

        this.setState({ isContinentModalLoading: true });

        const continent = {
            id: 0,
            name: this.state.newContinentName,
            isActive: true,
        };

        try {
            const result = await this.props.createContinent(continent);
            
            if (result.type === actionTypes.CREATE_CONTINENT_SUCCESS) {
                Notification('success', 'Save Successful', 'Continent created');
                this.setState({ 
                    isContinentModalLoading: false,
                    newContinentName: ''
                });
                this.continentModal.current.hide();
                await this.props.reloadFilters();
                // Set the newly created item as selected
                if (result.payload && result.payload.id) {
                    this.props.onFormChange('continentId', result.payload.id.toString());
                }
            } else {
                Notification('error', 'An Error Occurred', result.payload?.message || 'Failed to create Continent');
                this.setState({ isContinentModalLoading: false });
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error.message);
            this.setState({ isContinentModalLoading: false });
        }
    };

    createType = async () => {
        if (!this.validateCreateForm('type')) return false;

        this.setState({ isTypeModalLoading: true });

        const locationType = {
            id: 0,
            name: this.state.newTypeName,
            isActive: true,
        };

        try {
            const result = await this.props.createLocationType(locationType);
            
            if (result.type === actionTypes.CREATE_LOCATIONTYPE_SUCCESS) {
                Notification('success', 'Save Successful', 'Type created');
                this.setState({ 
                    isTypeModalLoading: false,
                    newTypeName: ''
                });
                this.typeModal.current.hide();
                await this.props.reloadFilters();
                // Set the newly created item as selected
                if (result.payload && result.payload.id) {
                    this.props.onFormChange('locationTypeId', result.payload.id.toString());
                }
            } else {
                Notification('error', 'An Error Occurred', result.payload?.message || 'Failed to create Type');
                this.setState({ isTypeModalLoading: false });
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error.message);
            this.setState({ isTypeModalLoading: false });
        }
    };

    validateCreateForm = (type) => {
        const fieldMap = {
            'managementCompany': { name: 'newManagementCompanyName', validState: 'isManagementCompanyFormValid' },
            'region': { name: 'newRegionName', validState: 'isRegionFormValid' },
            'area': { name: 'newAreaName', validState: 'isAreaFormValid' },
            'continent': { name: 'newContinentName', validState: 'isContinentFormValid' },
            'type': { name: 'newTypeName', validState: 'isTypeFormValid' }
        };

        const field = fieldMap[type];
        if (this.state[field.name].trim().length < 1) {
            this.setState({ [field.validState]: false });
            return false;
        } else {
            this.setState({ [field.validState]: true });
            return true;
        }
    };

    // Modal cancel handlers
    cancelManagementCompanyModal = () => {
        this.setState({ 
            newManagementCompanyName: '',
            newManagementCompanyCode: '', 
             isManagementCompanyFormValid: true,
            isManagementCompanyModalLoading: false
        });
    };
    cancelRegionModal = () => {
        this.setState({ 
            newRegionName: '', 
            isRegionFormValid: true,
            isRegionModalLoading: false
        });
    };
    cancelAreaModal = () => {
        this.setState({ 
            newAreaName: '', 
            isAreaFormValid: true,
            isAreaModalLoading: false
        });
    };
    cancelContinentModal = () => {
        this.setState({ 
            newContinentName: '', 
            isContinentFormValid: true,
            isContinentModalLoading: false
        });
    };

    cancelTypeModal = () => {
        this.setState({ 
            newTypeName: '', 
            isTypeFormValid: true 
        });
    };

    render() {
        return (
            <div className='location-form'>
                <form noValidate className={this.props.isFormValid ? '' : 'was-validated'}>
                    {/* Location/Team Information Section */}
                    <div className='mb-4'>
                        <h5 className='fw-bold mb-3'>Location/Team Information</h5>
                        <div className='row'>
                            <div className='col-md-6'>
                                <InputTextV6
                                    name='name'
                                    label='Location or Team Name'
                                    placeholder='Required'
                                    type='text'
                                    value={this.props.location.name}
                                    onChange={this.onFormChange}
                                    isRequired={true}
                                    errorMessage='Location or Team Name is required'
                                />
                                <MultiSelectBoxV6
                                    name='brandId'
                                    label='Brand'
                                    idField='id'
                                    valueField='name'
                                    options={this.props.brands}
                                    onChange={this.onSingleSelectBoxChange}
                                    placeholder='Required'
                                    selectedValues={this.props.location.brandId.toString()}
                                    isRequired={true}
                                    errorMessage='Brand is required'
                                    isMulti={false}
                                />
                                <MultiSelectBoxV6
                                    name='locationTypeId'
                                    label='Type'
                                    idField='id'
                                    valueField='name'
                                    options={this.getOptionsWithAddNew(this.props.locationTypes, 'Type')}
                                    onChange={this.onTypeChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.locationTypeId.toString()}
                                    isRequired={true}
                                    errorMessage='Type is required'
                                    isMulti={false}
                                />
                                <DatePickerV6
                                    className='mb-3'
                                    showIcon
                                    label='Opening Date'
                                    placeholder='When does/did this location open?'
                                    name='openingDate'
                                    value={this.props.location.openingDate}
                                    onChange={this.onDateChange}
                                    isRequired={true}
                                    errorMessage='Opening Date is required'
                                    dateFormat='d M yy'
                                />
                            </div>
                            <div className='col-md-6'>
                                <InputTextV6
                                    name='abbreviation'
                                    placeholder='Required'
                                    label='Abbreviation'
                                    type='text'
                                    value={this.props.location.abbreviation}
                                    onChange={this.onFormChange}
                                    isRequired={true}
                                    errorMessage='Abbreviation is required'
                                />
                                <MultiSelectBoxV6
                                    name='managementCompanyId'
                                    label='Management Company'
                                    idField='id'
                                    valueField='name'
                                    options={this.getOptionsWithAddNew(this.props.managementCompanies, 'Management Company')}
                                    onChange={this.onManagementCompanyChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.managementCompanyId.toString()}
                                    isRequired={true}
                                    errorMessage='Management Company is required'
                                    isMulti={false}
                                />
                                <MultiSelectBoxV6
                                    name='locationTagIds'
                                    options={this.props.tags}
                                    idField='id'
                                    valueField='name'
                                    label='Tags'
                                    placeholder='Select all that apply'
                                    selectedValues={this.props.location.locationTagIds.toString()}
                                    onChange={this.onMultiSelectBoxChange}
                                    isMulti={true}
                                />
                                <InputTextV6
                                    name='generation'
                                    placeholder='Optional'
                                    label='Generation'
                                    type='text'
                                    value={this.props.location.generation}
                                    onChange={this.onFormChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Geography Details Section */}
                    <div className='mb-4'>
                        <h5 className='fw-bold mb-3'>Geography Details</h5>
                        <div className='row'>
                            <div className='col-md-6'>
                                <MultiSelectBoxV6
                                    name='areaId'
                                    label='Area'
                                    idField='id'
                                    valueField='name'
                                    options={this.getOptionsWithAddNew(this.props.areas, 'Area')}
                                    onChange={this.onAreaChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.areaId.toString()}
                                    isRequired={true}
                                    errorMessage='Area is required'
                                    isMulti={false}
                                />
                                <MultiSelectBoxV6
                                    name='countryId'
                                    label='Country'
                                    idField='id'
                                    valueField='name'
                                    options={this.props.countries}
                                    onChange={this.onSingleSelectBoxChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.countryId.toString()}
                                    isRequired={true}
                                    errorMessage='Country is required'
                                    isMulti={false}
                                />
                                <InputTextV6
                                    name='address'
                                    label='Address'
                                    placeholder='Optional'
                                    type='text'
                                    value={this.props.location.address}
                                    onChange={this.onFormChange}
                                />
                                <InputTextV6
                                    name='stateProvince'
                                    label='State/Province'
                                    placeholder='Optional'
                                    type='text'
                                    value={this.props.location.stateProvince}
                                    onChange={this.onFormChange}
                                />
                            </div>
                            <div className='col-md-6'>
                                <MultiSelectBoxV6
                                    name='regionId'
                                    label='Region'
                                    idField='id'
                                    valueField='name'
                                    options={this.getOptionsWithAddNew(this.props.regions, 'Region')}
                                    onChange={this.onRegionChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.regionId.toString()}
                                    isRequired={true}
                                    errorMessage='Region is required'
                                    isMulti={false}
                                />
                                <MultiSelectBoxV6
                                    name='continentId'
                                    label='Continent'
                                    idField='id'
                                    valueField='name'
                                    options={this.getOptionsWithAddNew(this.props.continents, 'Continent')}
                                    onChange={this.onContinentChange}
                                    defaultValue='Required'
                                    selectedValues={this.props.location.continentId.toString()}
                                    isRequired={true}
                                    errorMessage='Continent is required'
                                    isMulti={false}
                                />
                                <InputTextV6
                                    name='city'
                                    label='City'
                                    placeholder='Optional'
                                    type='text'
                                    value={this.props.location.city}
                                    onChange={this.onFormChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Additional Details Section */}
                    <div className='mb-4'>
                        <h5 className='fw-bold mb-3'>Additional Details</h5>
                        <div className='row'>
                            <div className='col-md-6'>
                                <InputTextV6
                                    name='generalManagerName'
                                    placeholder='Optional but suggested'
                                    label='General Manager Name'
                                    type='text'
                                    value={this.props.location.generalManagerName}
                                    onChange={this.onFormChange}
                                />
                                <InputTextV6
                                    name='division'
                                    placeholder='Optional'
                                    label='Division'
                                    type='text'
                                    value={this.props.location.division}
                                    onChange={this.onFormChange}
                                />
                                <MultiSelectBoxV6
                                    name='projectManagerIds'
                                    label='Project Managers'
                                    idField='id'
                                    valueField='name'
                                    options={this.props.projectManagers}
                                    onChange={this.onMultiSelectBoxChange}
                                    placeholder='Optional'
                                    selectedValues={this.props.location.projectManagerIds.toString()}
                                    isRequired={false}
                                    isMulti={true}
                                />
                            </div>
                            <div className='col-md-6'>
                                <InputTextV6
                                    name='generalManagerEmail'
                                    placeholder='Optional but suggested'
                                    label='General Manager Email'
                                    type='email'
                                    value={this.props.location.generalManagerEmail}
                                    onChange={this.onFormChange}
                                    isValid={this.props.isGeneralManagerEmailValid}
                                />
                                <InputTextV6
                                    name='unit'
                                    label='Unit'
                                    placeholder='Optional'
                                    type='text'
                                    value={this.props.location.unit}
                                    onChange={this.onFormChange}
                                />
                                <InputTextV6
                                    name='billingInfo'
                                    label='Info'
                                    placeholder='Optional'
                                    type='text'
                                    value={this.props.location.billingInfo}
                                    onChange={this.onFormChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col mb-3'>
                            <ToggleV6 name='isActive' text='Active' checked={this.props.location.isActive} onChange={this.onToggleChange} />
                        </div>
                    </div>

                    {this.props.location.id ? (
                        <div>
                            <div className='row'>
                                <div className='col'>
                                    {this.props.isUserEhAdmin ? (
                                        <InputTextV6 label='Id' name='id' isDisabled={true} type='text' value={this.props.location.id.toString()} />
                                    ) : null}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-md-6'>
                                    <InputTextV6
                                        label='Created By'
                                        name='createdByName'
                                        isDisabled={true}
                                        type='text'
                                        value={this.props.location.createdByName}
                                    />
                                    <InputTextV6
                                        label='Created Date'
                                        name='createdDate'
                                        isDisabled={true}
                                        type='text'
                                        value={this.props.location.dateCreated}
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <InputTextV6
                                        label='Modified By'
                                        name='modifiedByName'
                                        isDisabled={true}
                                        type='text'
                                        value={this.props.location.modifiedByName}
                                    />
                                    <InputTextV6
                                        label='Modified Date'
                                        name='modifiedDate'
                                        isDisabled={true}
                                        type='text'
                                        value={this.props.location.dateModified}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {this.props.trackingUpdates && this.props.trackingUpdates.length > 0 && (
                        <div>
                            <div className='mb-1'>
                                <strong>Opening Date Changes</strong>
                            </div>
                            <table className='table table-striped'>
                                <thead>
                                    <tr>
                                        <th>Old Value</th>
                                        <th>New Value</th>
                                        <th>Changed By</th>
                                        <th>Changed On</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.trackingUpdates.map((update) => (
                                        <tr key={update.id}>
                                            <td>{Moment.utc(update.previousOpeningDate).format(DATE_FORMAT)}</td>
                                            <td>{Moment.utc(update.newOpeningDate).format(DATE_FORMAT)}</td>
                                            <td>{update.changedByName}</td>
                                            <td>{Moment.utc(update.changedDate).format(DATE_FORMAT)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </form>

                {/* Add New Management Company Modal */}
                <Modal61
                    ref={this.managementCompanyModal}
                    title='Create New Management Company'
                    isLoading={this.state.isManagementCompanyModalLoading}
                    onCloseClick={this.cancelManagementCompanyModal}
                    primaryButtonText='Save'
                    onPrimaryClick={this.createManagementCompany}
                    showSecondaryButton={true}
                    secondaryButtonText='Cancel'
                    onSecondaryClick={this.cancelManagementCompanyModal}
                    body={
                        <form name='newManagementCompanyForm' noValidate className={this.state.isManagementCompanyFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='management-company-name' className='form-label'>
                                    Management Company Name
                                </label>
                                <input
                                    id='management-company-name'
                                    name='management-company-name'
                                    type='text'
                                    className={`form-control ${!this.state.isManagementCompanyFormValid ? 'is-invalid' : ''}`}
                                    value={this.state.newManagementCompanyName}
                                    onChange={(e) => this.setState({ newManagementCompanyName: e.target.value })}
                                    required
                                    maxLength={100}
                                />
                                <div className='invalid-feedback'>Management Company Name is required</div>
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                            <div className='mb-3'>
                                <label htmlFor='management-company-code' className='form-label'>
                                    Company Code (Optional)
                                </label>
                                <input
                                    id='management-company-code'
                                    name='management-company-code'
                                    type='text'
                                    className='form-control'
                                    value={this.state.newManagementCompanyCode}
                                    onChange={(e) => this.setState({ newManagementCompanyCode: e.target.value })}
                                    maxLength={100}
                                />
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                        </form>
                    }
                />

                {/* Add New Region Modal */}
                <Modal61
                    ref={this.regionModal}
                    title='Create New Region'
                    isLoading={this.state.isRegionModalLoading}
                    onCloseClick={this.cancelRegionModal}
                    primaryButtonText='Save'
                    onPrimaryClick={this.createRegion}
                    showSecondaryButton={true}
                    secondaryButtonText='Cancel'
                    onSecondaryClick={this.cancelRegionModal}
                    body={
                        <form name='newRegionForm' noValidate className={this.state.isRegionFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='region-name' className='form-label'>
                                    Region Name
                                </label>
                                <input
                                    id='region-name'
                                    name='region-name'
                                    type='text'
                                    className={`form-control ${!this.state.isRegionFormValid ? 'is-invalid' : ''}`}
                                    value={this.state.newRegionName}
                                    onChange={(e) => this.setState({ newRegionName: e.target.value })}
                                    required
                                    maxLength={100}
                                />
                                <div className='invalid-feedback'>Region Name is required</div>
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                        </form>
                    }
                />

                {/* Add New Area Modal */}
                <Modal61
                    ref={this.areaModal}
                    title='Create New Area'
                    isLoading={this.state.isAreaModalLoading}
                    onCloseClick={this.cancelAreaModal}
                    primaryButtonText='Save'
                    onPrimaryClick={this.createArea}
                    showSecondaryButton={true}
                    secondaryButtonText='Cancel'
                    onSecondaryClick={this.cancelAreaModal}
                    body={
                        <form name='newAreaForm' noValidate className={this.state.isAreaFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='area-name' className='form-label'>
                                    Area Name
                                </label>
                                <input
                                    id='area-name'
                                    name='area-name'
                                    type='text'
                                    className={`form-control ${!this.state.isAreaFormValid ? 'is-invalid' : ''}`}
                                    value={this.state.newAreaName}
                                    onChange={(e) => this.setState({ newAreaName: e.target.value })}
                                    required
                                    maxLength={100}
                                />
                                <div className='invalid-feedback'>Area Name is required</div>
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                        </form>
                    }
                />

                {/* Add New Continent Modal */}
                <Modal61
                    ref={this.continentModal}
                    title='Create New Continent'
                    isLoading={this.state.isContinentModalLoading}
                    onCloseClick={this.cancelContinentModal}
                    primaryButtonText='Save'
                    onPrimaryClick={this.createContinent}
                    showSecondaryButton={true}
                    secondaryButtonText='Cancel'
                    onSecondaryClick={this.cancelContinentModal}
                    body={
                        <form name='newContinentForm' noValidate className={this.state.isContinentFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='continent-name' className='form-label'>
                                    Continent Name
                                </label>
                                <input
                                    id='continent-name'
                                    name='continent-name'
                                    type='text'
                                    className={`form-control ${!this.state.isContinentFormValid ? 'is-invalid' : ''}`}
                                    value={this.state.newContinentName}
                                    onChange={(e) => this.setState({ newContinentName: e.target.value })}
                                    required
                                    maxLength={100}
                                />
                                <div className='invalid-feedback'>Continent Name is required</div>
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                        </form>
                    }
                />

                {/* Add New Type Modal */}
                <Modal61
                    ref={this.typeModal}
                    title='Create New Type'
                    isLoading={this.state.isTypeModalLoading}
                    onCloseClick={this.cancelTypeModal}
                    primaryButtonText='Save'
                    onPrimaryClick={this.createType}
                    showSecondaryButton={true}
                    secondaryButtonText='Cancel'
                    onSecondaryClick={this.cancelTypeModal}
                    body={
                        <form name='newTypeForm' noValidate className={this.state.isTypeFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='type-name' className='form-label'>
                                    Type Name
                                </label>
                                <input
                                    id='type-name'
                                    name='type-name'
                                    type='text'
                                    className={`form-control ${!this.state.isTypeFormValid ? 'is-invalid' : ''}`}
                                    value={this.state.newTypeName}
                                    onChange={(e) => this.setState({ newTypeName: e.target.value })}
                                    required
                                    maxLength={100}
                                />
                                <div className='invalid-feedback'>Type Name is required</div>
                                <small className='form-text text-muted'>Character Limit 100</small>
                            </div>
                        </form>
                    }
                />
            </div>
        );
    }
}

LocationForm.propTypes = {
    isFormValid: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    isUserEhAdmin: PropTypes.bool.isRequired,
    areas: PropTypes.array.isRequired,
    brands: PropTypes.array.isRequired,
    continents: PropTypes.array.isRequired,
    countries: PropTypes.array.isRequired,
    locationTypes: PropTypes.array.isRequired,
    managementCompanies: PropTypes.array.isRequired,
    regions: PropTypes.array.isRequired,
    projectManagers: PropTypes.array.isRequired,
    onFormChange: PropTypes.func.isRequired,
    isGeneralManagerEmailValid: PropTypes.bool.isRequired,
    trackingUpdates: PropTypes.array,
    tags: PropTypes.array,
    // Add New functionality props
    createManagementCompany: PropTypes.func.isRequired,
    createRegion: PropTypes.func.isRequired,
    createArea: PropTypes.func.isRequired,
    createContinent: PropTypes.func.isRequired,
    createLocationType: PropTypes.func.isRequired,
    reloadFilters: PropTypes.func.isRequired,
};
